import {
  Collapse,
  IconButton,
  List,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from "@mui/material"
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft"
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight"
import { useDebounceValue } from "usehooks-ts"
import { useEffect, useMemo, useState } from "react"
import CloseIcon from "@mui/icons-material/Close"
import ListItem from "@mui/material/ListItem"
import Grid from "@mui/material/Grid"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import { ZoomIn, ZoomOut } from "@mui/icons-material"
import Button from "@mui/material/Button"
import { formatValuePythonic } from "@utils/format/pythonicFormat"
import { AppNode } from "@features/builder/graph/types"
import { Edge, useNodes, useStoreApi } from "@xyflow/react"
import { nodesGroupMapper } from "@features/builder/helpers/nodesGroupMapper"
import { InfoBox } from "@features/builder/TopActionBar/SearchPooper/InfoBox"

import { useGoToNode } from "@features/builder/hooks/useGoToNode"
import SearchTabButtons from "@features/builder/TopActionBar/SearchPooper/SearchTabButtons"
import { SearchNodeType, SearchType, searchNodes } from "@utils/node/search"

type SearchPopperProps = {
  onCloseClick: () => void
}

export const SearchPopper = (props: SearchPopperProps) => {
  const graphStore = useStoreApi<AppNode, Edge>()
  const graphState = graphStore.getState()
  const nodes = useNodes<AppNode>()
  const mapper = useMemo(() => nodesGroupMapper(nodes), [nodes])
  const goToNode = useGoToNode()

  const [debouncedValue, setValue] = useDebounceValue("", 300)
  const [searchResult, setSearchResult] = useState<{
    nodes: SearchNodeType[]
    currentIndex: number
  }>({
    nodes: [],
    currentIndex: 0,
  })
  const [zoomLevel, setZoomLevel] = useState(2.5)
  const [searchType, setSearchType] = useState<SearchType>("label")

  // link is exception and we want to show all nodes with link even if search is empty
  const isSearching = debouncedValue !== "" || searchType === "link"

  useEffect(() => {
    if (!isSearching) {
      setSearchResult({
        nodes: [],
        currentIndex: 0,
      })
      return
    }

    const newNodes = searchNodes(mapper, searchType, debouncedValue)

    setSearchResult((prevState) => {
      return {
        nodes: newNodes,
        // Only reset index if search results changed significantly
        currentIndex:
          newNodes.length > 0 && prevState.currentIndex > 0 ? prevState.currentIndex : 0,
      }
    })
  }, [debouncedValue, graphState.nodeLookup, mapper, searchType, zoomLevel, isSearching])

  const selectNodeIndex = (index: number) => {
    if (index === 0) return

    const searchNode = searchResult.nodes[index - 1]
    if (!searchNode) return

    const node = graphState.nodeLookup.get(searchNode.nodeId)
    if (!node) return

    goToNode(node.id)
  }

  const onPrevClick = () => {
    if (searchResult.currentIndex <= 0) return

    setSearchResult({
      ...searchResult,
      currentIndex: searchResult.currentIndex - 1,
    })
    selectNodeIndex(searchResult.currentIndex - 1)
  }

  const onNextClick = () => {
    if (searchResult.currentIndex >= searchResult.nodes.length) return

    setSearchResult({
      ...searchResult,
      currentIndex: searchResult.currentIndex + 1,
    })
    selectNodeIndex(searchResult.currentIndex + 1)
  }

  const incrementZoom = () => {
    setZoomLevel(zoomLevel + 0.25)
  }

  const decrementZoom = () => {
    setZoomLevel(zoomLevel - 0.25)
  }

  const handleListItemClick = (i: number) => {
    setSearchResult((currentState) => ({
      ...currentState,
      currentIndex: i + 1,
    }))
    selectNodeIndex(i + 1)
  }

  const handleArrowUpDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case "ArrowUp":
        onPrevClick()
        break
      case "ArrowDown":
        onNextClick()
        break
    }
  }

  return (
    <Paper
      sx={{
        p: 1,
        mt: "1px",
        ml: "-2px",
        boxShadow:
          "0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)",
        borderRadius: "5px !important",
        border: "1px solid #ccc !important",
      }}
    >
      <Grid container sx={{ marginBottom: "1px" }}>
        <SearchTabButtons value={searchType} onChange={(newType) => setSearchType(newType)} />
      </Grid>
      <TextField
        label={`Search ${searchType}s`}
        variant="standard"
        sx={{ width: "300px" }}
        onChange={(event) => setValue(event.target.value)}
        onKeyUp={handleArrowUpDown}
      />

      <IconButton
        aria-label="Previous"
        sx={{ verticalAlign: "bottom" }}
        disabled={searchResult.nodes.length === 0}
        onClick={onPrevClick}
      >
        <KeyboardDoubleArrowLeftIcon />
      </IconButton>
      <IconButton
        aria-label="Next"
        sx={{ verticalAlign: "bottom" }}
        disabled={searchResult.nodes.length === 0}
        onClick={onNextClick}
      >
        <KeyboardDoubleArrowRightIcon />
      </IconButton>

      <IconButton aria-label="Close" sx={{ verticalAlign: "bottom" }} onClick={props.onCloseClick}>
        <CloseIcon />
      </IconButton>
      <Card
        sx={{
          width: "100%",
          maxHeight: "300px",
          overflowY: "scroll",
          boxShadow: "none",
          borderRadius: "5px !important",
        }}
      >
        <CardContent
          sx={{
            paddingBottom: "5px !important",
            padding: "5px",
            mb: "0px",
            mt: "0px",
            cursor: "pointer",
            borderBottom: "1px solid #e7e7e7",
          }}
        >
          <Typography
            variant={"caption"}
          >{`${searchResult.nodes.length} of ${nodes.length} nodes`}</Typography>
        </CardContent>
        <Collapse in={searchResult.nodes.length >= 1} timeout="auto" unmountOnExit>
          <CardContent
            sx={{
              paddingBottom: "5px !important",
              padding: "3px",
              mb: "0px",
              mt: "0px",
              cursor: "pointer",
            }}
          >
            <Grid>
              <List>
                {searchResult.nodes.map((searchNode, i) => (
                  <ListItem
                    divider
                    key={searchNode.nodeId}
                    onClick={() => handleListItemClick(i)}
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "rgba(225, 225, 225, 0.4)",
                      },
                      backgroundColor:
                        i + 1 === searchResult.currentIndex ? "rgba(225, 225, 225, 0.4)" : "",
                    }}
                  >
                    <ListItemText
                      primary={
                        <span style={{ fontSize: "1rem" }}>
                          {graphState.nodeLookup.get(searchNode.nodeId)?.data.label}
                        </span>
                      }
                      secondary={
                        <span style={{ fontSize: "1rem" }}>
                          {formatValuePythonic("", searchNode.format)}
                        </span>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
          </CardContent>
        </Collapse>
      </Card>
      <Grid
        container
        sx={{
          p: "3px",
          color: "#777",
          wordBreak: "break-word",
          wordWrap: "break-word",
          maxWidth: "450px",
        }}
      >
        {!isSearching && <InfoBox />}

        <Grid item xs={8} sx={{ marginTop: "4px" }}>
          <Typography>Zoom level on navigation: {zoomLevel.toFixed(2)}x</Typography>
        </Grid>
        <Grid item xs={2}>
          <Button onClick={decrementZoom} sx={{ width: "10px" }} endIcon={<ZoomOut />}></Button>
        </Grid>
        <Grid item xs={2}>
          <Button onClick={incrementZoom} sx={{ width: "10px" }} endIcon={<ZoomIn />}></Button>
        </Grid>
      </Grid>
    </Paper>
  )
}
